import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

interface Company {
  id: string;
  name: string;
  plan: string;
  logo: string | null;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  company: Company;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      mustChangePassword: false,

      login: async (email: string, password: string) => {
        const response = await api.post('/auth/login', { email, password });
        const { accessToken, refreshToken, user, must_change_password } = response.data.data;

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        if (must_change_password) {
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: false,
            mustChangePassword: true,
            isLoading: false,
          });
        } else {
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            mustChangePassword: false,
            isLoading: false,
          });
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          // Ignore
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          mustChangePassword: false,
          isLoading: false,
        });
      },

      loadUser: async () => {
        const token = localStorage.getItem('accessToken');

        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.get('/auth/me');
          const userData = response.data.data;
          set({
            user: {
              id: userData.id,
              email: userData.email,
              name: userData.name,
              role: userData.role,
              company: userData.company,
            },
            accessToken: token,
            refreshToken: localStorage.getItem('refreshToken'),
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
